import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Eyebrow } from "@/components/Eyebrow";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Construction, User as UserIcon } from "lucide-react";

function AdviserStrip() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/portal/my-adviser").then((r) => setData(r.data)).catch(() => setData({ adviser: null, summary_notes: [] })); }, []);
  if (!data) return null;
  return (
    <div className="border border-stone/40 bg-bone-dim/30 p-6 mt-8" data-testid="dashboard-adviser-strip">
      <div className="flex items-baseline gap-2 mb-3"><UserIcon className="h-4 w-4 text-brass" /><div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Your adviser</div></div>
      {data.adviser ? (
        <>
          <div className="text-ivy font-serif text-lg tracking-tight">{data.adviser.name}</div>
          <div className="text-xs text-stone">{data.adviser.email}</div>
          {data.summary_notes?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-stone/30 space-y-3" data-testid="dashboard-adviser-notes">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Recent notes</div>
              {data.summary_notes.map((n) => (
                <div key={n.note_id} className="text-sm text-ink border-l-2 border-brass pl-3">
                  <div className="whitespace-pre-wrap">{n.body_markdown}</div>
                  <div className="text-[10px] text-stone mt-1 uppercase tracking-[0.14em]">{(n.created_at || "").slice(0, 10)}</div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-ink-soft">You&rsquo;ll be matched with an adviser after your first consultation.</p>
      )}
    </div>
  );
}

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);

  useEffect(() => {
    if (location.state?.user) return;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) return;
    const check = async () => {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
      } catch {
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [location.state, navigate]);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
      toast.success("Signed out");
      navigate("/", { replace: true });
    } catch {
      toast.error("Logout failed");
    }
  };

  if (loading) {
    return (
      <Layout withFooter={false}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-ink-soft text-sm uppercase tracking-[0.18em]">Loading your dashboard…</div>
        </div>
      </Layout>
    );
  }
  if (!user) return null;

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-16 lg:py-24" data-testid="dashboard-page">
        <Eyebrow>Your account</Eyebrow>
        <h1 className="font-serif font-medium text-4xl sm:text-5xl text-ink leading-tight tracking-tight mt-4">
          Welcome, {user.name?.split(" ")[0] || user.email}.
        </h1>
        <p className="text-ink-soft mt-4 max-w-xl">
          You're signed in. Below is the bare minimum we need for Phase 1 — your real student portal arrives in Phase 2.
        </p>

        <AdviserStrip />

        <div className="border border-ivy bg-ivy text-bone mt-12 p-7 lg:p-8 flex items-start gap-5" data-testid="portal-coming-soon-banner">
          <Construction className="h-6 w-6 text-brass-soft flex-shrink-0 mt-0.5" />
          <div>
            <Eyebrow tone="bone" className="text-brass-soft">Portal status</Eyebrow>
            <p className="font-serif font-medium text-xl mt-2 tracking-tight">Your portal is coming soon — Phase 2 of the build.</p>
            <p className="text-bone-dim/80 text-sm mt-3 leading-relaxed">
              School list builder, milestone tracker, document vault, and recommender tracker land here next.
            </p>
          </div>
        </div>

        <div className="border border-stone/40 bg-bone mt-6 p-7" data-testid="account-card">
          <Eyebrow>Account</Eyebrow>
          <div className="mt-4 space-y-2.5 text-sm border-t border-stone/30 pt-4">
            <div className="flex gap-3">
              <span className="text-[11px] uppercase tracking-[0.2em] text-stone w-24 mt-0.5">Name</span>
              <span className="font-medium text-ink">{user.name}</span>
            </div>
            <div className="flex gap-3">
              <span className="text-[11px] uppercase tracking-[0.2em] text-stone w-24 mt-0.5">Email</span>
              <span className="text-ink" data-testid="user-email">{user.email}</span>
            </div>
            <div className="flex gap-3 items-center">
              <span className="text-[11px] uppercase tracking-[0.2em] text-stone w-24">Role</span>
              <span data-testid="user-role" className="font-mono text-[11px] px-2 py-0.5 bg-bone-dim border border-stone/30">
                {user.role}
              </span>
            </div>
            <div className="flex gap-3 items-center">
              <span className="text-[11px] uppercase tracking-[0.2em] text-stone w-24">Tier</span>
              <span data-testid="user-tier" className="font-mono text-[11px] px-2 py-0.5 bg-bone-dim border border-stone/30">
                {user.tier ?? "null"}
              </span>
            </div>
            <div className="flex gap-3 pt-2">
              <span className="text-[11px] uppercase tracking-[0.2em] text-stone w-24 mt-0.5">user_id</span>
              <code className="text-[11px] font-mono text-ink-soft">{user.user_id}</code>
            </div>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              onClick={() => navigate("/services")}
              className="border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-5 transition-colors"
            >
              See services
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-ink-soft hover:text-ivy uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-3 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
